import React, { useState, useEffect } from 'react';
import { IRunSheetProps, IDynamicForm, Answer, IRunSheetQuestion } from './types';
import { Formik, Form } from 'formik';
import { FormSectionHeading, ActionButton } from '../../forms';
import Question from './question';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import createDynamicForm from './createDynamicForm';
import { QuestionTypes } from '../../../constants';
import toUtcDate from '../../../utils/toUtcDate';

const RunSheet: React.FC<IRunSheetProps> = ({
    busy,
    isCancelled,
    isOwner,
    resultsSubmitted,
    groups,
    pdfUrl,
    requiresAttention,
    runDate,
    onReportUpdated,
    handleFetchingOverallAssessmentGrades,
    fetchingOverallAssessmentGrades
}) => {
    const [dynamicForm, setDynamicForm] = useState<IDynamicForm>();
    const [overallGrade, setOverallGrade] = useState<IRunSheetQuestion>();
    const [isAppointmentInFuture, setIsAppointmentInFuture] = useState<boolean>(false);

    // Parse date from "dd/MM/yyyy" format
    const parseDateFromDDMMYYYY = (dateStr: string): Date | null => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
    };

    // Check if appointment date is in the future
    useEffect(() => {
        if (runDate) {
            const appointmentDate = parseDateFromDDMMYYYY(runDate);
            if (appointmentDate) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                appointmentDate.setHours(0, 0, 0, 0);
                setIsAppointmentInFuture(appointmentDate > today);
            } else {
                setIsAppointmentInFuture(true);
            }
        } else {
            setIsAppointmentInFuture(true);
        }
    }, [runDate]);

    useEffect(() => {
        if (groups) {
            setDynamicForm(createDynamicForm(groups));

            const resultGroup = groups.filter(function (group) {
                return group.heading.toLowerCase().includes('result');
            }).at(0);

            if (resultGroup) {
                const overallGrade = resultGroup.questions.filter(function (question) {
                    return question.text.toLowerCase().includes('overall') && question.text.toLowerCase().includes('grade');
                }).at(0);

                if (overallGrade) {
                    setOverallGrade(overallGrade);
                }
            }
        }
    }, [groups]);

    const handleUpdateResults = (values: any, complete: boolean, forOverallGrade: boolean): void => {
        if (!dynamicForm?.fieldMappings) return;

        const results: Array<Answer> = [];

        Object.keys(values).forEach(key => {
            const questionFileId: number = Number.parseInt(key);
            const fieldMapping = dynamicForm.fieldMappings[questionFileId];

            const result: Answer = { fileId: questionFileId, answer: null, fieldtype: fieldMapping.type };

            if (values[questionFileId] !== null) {
                switch (fieldMapping.type) {
                    case QuestionTypes.DateTime:
                        result.answer =
                            values[key] instanceof Date
                                ? toUtcDate(values[key]).toISOString()
                                : values[key];
                        break;

                    case QuestionTypes.Check:
                        result.answer = values[key] ? '1' : '0';
                        break;

                    default:
                        result.answer = values[key].toString();
                }
            }

            results.push(result);
        });

        if (forOverallGrade) {
            handleFetchingOverallAssessmentGrades(results, complete);
        } else {
            onReportUpdated(results, complete);
        }
    };

    const isFormDisabled = busy || requiresAttention || resultsSubmitted || isCancelled || !isOwner || isAppointmentInFuture;

    return (
        <>
            {dynamicForm?.initialValues && (
                <Formik
                    initialValues={dynamicForm.initialValues}
                    validationSchema={dynamicForm.validationSchema}
                    onSubmit={values => handleUpdateResults(values, true, false)}
                >
                    {({ values }) => (
                        <Form>
                            <fieldset disabled={isFormDisabled}>
                                {groups.map(group => (
                                    <div key={group.heading}>
                                        <FormSectionHeading>{group.heading}</FormSectionHeading>
                                        {group.questions.map(question => (
                                            <Question key={question.fileId} {...question}
                                                enablestatus={!isFormDisabled}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </fieldset>
                            <div className="text-right">
                                {overallGrade && (
                                    <ActionButton
                                        busy={fetchingOverallAssessmentGrades}
                                        type="button"
                                        cssClass="e-primary"
                                        disabled={isFormDisabled}
                                        onClick={() => handleUpdateResults(values, false, true)}
                                    >
                                        Fetch Overall Assessment Grades
                                    </ActionButton>
                                )}
                                {pdfUrl && (
                                    <ButtonComponent
                                        type="button"
                                        cssClass="e-link mr-1"
                                        disabled={busy}
                                        onClick={() => window.open(pdfUrl, '_blank')}
                                    >
                                        Show PDF
                                    </ButtonComponent>
                                )}
                                {!resultsSubmitted && !requiresAttention && !isCancelled && isOwner && !isAppointmentInFuture && (
                                    <>
                                        <ActionButton
                                            busy={busy}
                                            type="button"
                                            cssClass="e-success"
                                            onClick={() => handleUpdateResults(values, false, false)}
                                        >
                                            Update Results
                                        </ActionButton>
                                        <ButtonComponent
                                            type="submit"
                                            cssClass="e-danger ml-1"
                                            disabled={busy || resultsSubmitted}
                                        >
                                            Submit & Finalise Results
                                        </ButtonComponent>
                                    </>
                                )}
                            </div>

                            {resultsSubmitted && <p>This report has been submitted.</p>}
                            {isCancelled && <p>This run has been cancelled</p>}
                            {!resultsSubmitted && !isCancelled && !isOwner && <p>You are not the currently assigned observer of this run.</p>}
                            {!resultsSubmitted && !isCancelled && isOwner && isAppointmentInFuture && <p>The run sheet cannot be completed until the appointment date has passed.</p>}

                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

export default RunSheet;
